import { css } from "@emotion/css";
import { CheckCircleFilledIcon } from "@octopusdeploy/design-system-icons";
import { borderWidth, borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type React from "react";
import type { ParentEnvironmentConfig, RunbookConfig } from "./EphemeralEnvironmentsConfigWizard";
interface ReviewContentRowProps {
    title: string;
    value: string;
    accessory?: React.ReactNode;
}
function ReviewContentRow({ title, value, accessory }: ReviewContentRowProps) {
    const styles = {
        container: css({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: `${space[12]} ${space[16]}`,
            borderBottom: `${borderWidth[1]} solid ${themeTokens.color.background.tertiary}`,
            "&:last-child": {
                borderBottom: "none",
            },
        }),
        content: css({
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: space[8],
        }),
    };
    return (<div className={styles.container}>
            <div className={styles.content}>
                <div>{title}</div>
                <div>
                    <b>{value}</b>
                </div>
            </div>
            {accessory && <CheckCircleFilledIcon size={36} color={themeTokens.color.icon.success}/>}
        </div>);
}
interface ReviewContentTableProps {
    children?: React.ReactNode;
}
function ReviewContentTable({ children }: ReviewContentTableProps) {
    const styles = {
        table: css({
            border: `${borderWidth[1]} solid ${themeTokens.color.background.tertiary}`,
            borderRadius: borderRadius.small,
            overflow: "hidden",
        }),
    };
    return <div className={styles.table}>{children}</div>;
}
export interface ReviewContentProps {
    isSuccessStatus?: boolean;
    parentEnvironmentConfig: ParentEnvironmentConfig;
    nameTemplateConfig: string;
    provisioningConfig?: RunbookConfig;
    deprovisioningConfig?: RunbookConfig;
    automaticEphemeralEnvironmentDeployments?: boolean;
    branch?: string;
}
export function ReviewContent({ isSuccessStatus, parentEnvironmentConfig, nameTemplateConfig, provisioningConfig, deprovisioningConfig, automaticEphemeralEnvironmentDeployments, branch }: ReviewContentProps) {
    if (!parentEnvironmentConfig.name) {
        throw new Error("Missing parent environment configuration.");
    }
    return (<ReviewContentTable>
            <ReviewContentRow title="Parent environment" value={parentEnvironmentConfig.name} accessory={isSuccessStatus}/>
            <ReviewContentRow title="Auto deployments" value={automaticEphemeralEnvironmentDeployments ? "Automatically deploy to ephemeral environments" : "Don't automatically deploy to ephemeral environments"} accessory={isSuccessStatus}/>
            {automaticEphemeralEnvironmentDeployments && <ReviewContentRow title="Environment name template" value={nameTemplateConfig} accessory={isSuccessStatus}/>}
            <ReviewContentRow title="Provisioning runbook" value={provisioningConfig?.name || "None selected"} accessory={isSuccessStatus}/>
            <ReviewContentRow title="Deprovisioning runbook" value={deprovisioningConfig?.name || "None selected"} accessory={isSuccessStatus}/>
            {branch && <ReviewContentRow title="Branch" value={branch} accessory={isSuccessStatus}/>}
        </ReviewContentTable>);
}
